import React, {useState} from 'react';
import {
    Box,
    Button,
    Dialog, DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton, TextField,
    Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import {AdminAction, Key, User} from "../types";
import {useToast} from "use-toast-mui";
import {useBlockUser} from "../hooks/useBlockUser";
import {useUnblockUser} from "../hooks/useUnblockUser";
import {useResetKey} from "../hooks/useResetKey";
import {useResetCredits} from "../hooks/useResetCredits";
import {useAddKey} from "../hooks/useAddKey";
import {useUpdateUserEmail} from "../hooks/useUpdateUserEmail";
import {useHandleCopy} from "../hooks/useHandleCopy";
import {useExecute} from "../hooks/useExecute";

interface IAdminUserInfoCardProps {
    user: User;
    setUser: (user: User | null) => void;
}

export const AdminUserInfoCard = ({user, setUser}: IAdminUserInfoCardProps) => {
    const toast = useToast();
    const {blockUser} = useBlockUser();
    const {unblockUser} = useUnblockUser();
    const {addKey} = useAddKey();
    const {resetKey} = useResetKey();
    const {resetCredits} = useResetCredits();
    const {handleCopy} = useHandleCopy();
    const {updateUserEmail} = useUpdateUserEmail();
    const {execute, isLoading} = useExecute({user, setUser});
    const [isOtherEmailDialogOpen, setIsOtherEmailDialogOpen] = useState(false);
    const [isBillingEmailDialogOpen, setIsBillingEmailDialogOpen] = useState(false);
    const [billingEmail, setBillingEmail] = useState('');
    const [otherEmail, setOtherEmail] = useState('');

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                border: '1px solid grey',
                borderRadius: '5px',
                mt: 4,
                p: 4
            }}>
                <Typography variant="body1" mx="auto">
                    Billing email (default - used for log in): <span style={{fontStyle: 'italic'}}>{user.billingEmail}</span>
                    <IconButton aria-label="copy"
                                size="small"
                                onClick={() => {
                                    setBillingEmail(user.billingEmail);
                                    setIsBillingEmailDialogOpen(true);
                                }}
                                disabled={isLoading}>
                        <EditIcon fontSize="inherit"/>
                    </IconButton>
                    <IconButton aria-label="copy" size="small" onClick={() => handleCopy(user.billingEmail)}>
                        <ContentCopyIcon fontSize="inherit"/>
                    </IconButton>
                </Typography>
                <Typography variant="body1" mx="auto">
                    Other email (optional): <span style={{fontStyle: 'italic'}}>{user.email}</span>
                    <IconButton aria-label="copy"
                                size="small"
                                onClick={() => {
                                    setOtherEmail(user.email);
                                    setIsOtherEmailDialogOpen(true);
                                }}
                                disabled={isLoading}>
                        <EditIcon fontSize="inherit"/>
                    </IconButton>
                    <IconButton aria-label="copy" size="small" onClick={() => handleCopy(user.email)}>
                        <ContentCopyIcon fontSize="inherit"/>
                    </IconButton>
                </Typography>
                <Box
                    sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', mb: 4}}>
                    <Typography variant="body1">
                        Status: {user.status}
                    </Typography>
                    {
                        user.status === 'BLOCKED' &&
                        <Button variant="outlined"
                                size="small"
                                sx={{textTransform: 'none', ml: 1}}
                                onClick={() => execute(unblockUser)}
                                disabled={isLoading}>
                            unblock
                        </Button>
                    }
                    {
                        user.status !== 'BLOCKED' &&
                        <Button variant="outlined"
                                size="small"
                                sx={{textTransform: 'none', ml: 1}}
                                onClick={() => execute(blockUser)}
                                disabled={isLoading}>
                            block
                        </Button>
                    }
                </Box>
                {
                    user.keys.map((key: Key) =>
                        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', my: 1}}>
                            <Typography variant="body1">
                                <span style={{fontStyle: 'italic'}}>{key.value}</span>
                                <IconButton aria-label="copy" size="small" onClick={() => handleCopy(key.value)}>
                                    <ContentCopyIcon fontSize="inherit"/>
                                </IconButton>
                                - {key.status}
                            </Typography>
                            <Button variant="outlined" size="small"
                                    disabled={key.status === 'NOT_USED' || user.status === 'BLOCKED' || isLoading}
                                    sx={{textTransform: 'none', ml: 1}}
                                    onClick={() => execute(resetKey, key.value)}>
                                reset key
                            </Button>
                        </Box>
                    )
                }
                {
                    user.keys.length <= 1 &&
                    <Button variant="outlined"
                            size="small"
                            sx={{textTransform: 'none', mx: 3}}
                            onClick={() => execute(addKey)}
                            disabled={isLoading}>
                        generate another key
                    </Button>
                }
                <Box
                    sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', mt: 5}}>
                    <Typography variant="body1">
                        <span style={{fontStyle: 'italic'}}>{user.usedCredits}/{user.totalCredits} credits</span> used
                        this month
                    </Typography>
                    <Button variant="outlined"
                            size="small"
                            disabled={user.usedCredits <= 0 || isLoading}
                            sx={{textTransform: 'none', ml: 1}}
                            onClick={() => execute(resetCredits)}>
                        reset credits
                    </Button>
                </Box>
            </Box>
            <Dialog open={isBillingEmailDialogOpen} onClose={() => setIsBillingEmailDialogOpen(false)}>
                <DialogTitle>Update Billing Email (used for log in)</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        IMPORTANT: This action will update the user's log in email. The old email will be put in Other email field for reference.
                    </DialogContentText>
                    <TextField
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="standard"
                        value={billingEmail}
                        onChange={(e) => setBillingEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={async () => {
                        setIsBillingEmailDialogOpen(false);
                        const validatedBillingEmail = billingEmail?.trim();
                        if (validatedBillingEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(validatedBillingEmail)) {
                            const updateEmail: AdminAction = (key: string) => updateUserEmail(key, user.billingEmail, billingEmail);
                            await execute(updateEmail);
                        } else {
                            toast.error("Please enter a valid email.");
                        }
                    }}>Save</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isOtherEmailDialogOpen} onClose={() => setIsOtherEmailDialogOpen(false)}>
                <DialogTitle>Update User Email</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This action will update user's secondary email.
                    </DialogContentText>
                    <TextField
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="standard"
                        value={otherEmail}
                        onChange={(e) => setOtherEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={async () => {
                        setIsOtherEmailDialogOpen(false);
                        const validatedEmail = otherEmail?.trim();
                        if (validatedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(validatedEmail)) {
                            const updateEmail: AdminAction = (key: string) => updateUserEmail(key, otherEmail);
                            await execute(updateEmail);
                        } else {
                            toast.error("Please enter a valid email.");
                        }
                    }}>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
